function boldWrap(text: string): string {
  if (!text) return text;
  if (text.startsWith("**") && text.endsWith("**")) return text;
  return `**${text}**`;
}

// Google Docs/Word paste HTML marks bold via inline font-weight styles rather
// than <b>/<strong> tags, so both need to be checked.
function isBold(el: HTMLElement): boolean {
  if (el.tagName === "B" || el.tagName === "STRONG") return true;
  const fontWeight = el.style.fontWeight;
  if (!fontWeight) return false;
  const numericWeight = parseInt(fontWeight, 10);
  return Number.isNaN(numericWeight) ? fontWeight.toLowerCase() === "bold" : numericWeight >= 600;
}

function indexAmongSiblings(el: HTMLElement): number {
  return Array.from(el.parentElement?.children ?? []).indexOf(el);
}

function walk(node: ChildNode): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? "";
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }

  const el = node as HTMLElement;
  const inner = Array.from(el.childNodes).map(walk).join("");
  const wrapped = isBold(el) ? boldWrap(inner) : inner;

  switch (el.tagName) {
    case "H1":
    case "H2":
    case "H3":
    case "H4":
    case "H5":
    case "H6":
      return `\n\n${boldWrap(wrapped.trim())}\n\n`;
    case "LI": {
      const prefix = el.parentElement?.tagName === "OL" ? `${indexAmongSiblings(el) + 1}. ` : "- ";
      return `\n${prefix}${wrapped.trim()}`;
    }
    case "UL":
    case "OL":
      return `\n${wrapped}\n`;
    case "BR":
      return "\n";
    case "P":
    case "DIV":
      return `\n${wrapped.trim()}\n`;
    default:
      return wrapped;
  }
}

// Converts clipboard HTML into plain text that keeps bold/heading/list structure
// visible as markdown, since a native <textarea> can't render rich formatting.
export function htmlToMarkdown(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const raw = Array.from(doc.body.childNodes).map(walk).join("");
  return raw.replace(/\n{3,}/g, "\n\n").trim();
}
